// AdminPromptsPanel — edit the 3 interview prompts (pre_screening,
// psychoanalytical, final_interview), with optional per-company override.
//
// Routes used:
//   GET   /api/admin/prompts/:kind?companyId=...
//   PUT   /api/admin/prompts/:kind?companyId=...
//   POST  /api/admin/prompts/:kind/reset?companyId=...
//   GET   /api/companies         (to populate company picker — falls back to free-text)

import React, { useEffect, useState } from "react";
import { Save, RotateCcw, Loader2 } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";

const KINDS = [
  { key: "pre_screening",          label: "Pre-screening — CV Verification (Mira)" },
  { key: "psychoanalytical",       label: "Psychoanalytical — Mindset & Culture (Dr. Patel)" },
  { key: "final_interview",        label: "Final Interview — Technical + HR (Senior Panel)" },
  { key: "gen_ai_readiness",       label: "Gen AI Readiness Interview (Nova)" },
  { key: "situational_judgement",  label: "Situational Judgement Test" },
];

export default function AdminPromptsPanel() {
  const [kind, setKind]           = useState(KINDS[0].key);
  const [companyId, setCompanyId] = useState(""); // "" = global default
  const [companies, setCompanies] = useState([]);
  const [prompt, setPrompt]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [axesInput, setAxesInput] = useState("");

  // Load companies for picker (optional — silent fail)
  useEffect(() => {
    api.get("/companies").then(r => setCompanies(r.data?.data || r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const url = companyId ? `/admin/prompts/${kind}?companyId=${companyId}` : `/admin/prompts/${kind}`;
      const r = await api.get(url);
      setPrompt(r.data);
      setAxesInput((r.data?.gradingAxes || []).join(", "));
    } catch (e) {
      toast.error("Failed to load prompt");
      setPrompt(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, companyId]);

  const save = async () => {
    if (!prompt) return;
    setSaving(true);
    try {
      const payload = {
        label:           prompt.label,
        systemPrompt:    prompt.systemPrompt,
        opener:          prompt.opener,
        questionTarget:  prompt.questionTarget,
        gradingAxes:     axesInput.split(",").map(s => s.trim()).filter(Boolean),
        durationMinutes: prompt.durationMinutes,
      };
      const url = companyId ? `/admin/prompts/${kind}?companyId=${companyId}` : `/admin/prompts/${kind}`;
      const r = await api.put(url, payload);
      setPrompt(r.data);
      toast.success(companyId ? "Company override saved" : "Global prompt saved");
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(companyId
      ? "Remove the company override? The global default will apply."
      : "Reset to the shipped default? Edits will be lost.")) return;
    setSaving(true);
    try {
      const url = companyId ? `/admin/prompts/${kind}/reset?companyId=${companyId}` : `/admin/prompts/${kind}/reset`;
      await api.post(url);
      await load();
      toast.success(companyId ? "Override removed" : "Reset to default");
    } catch (e) {
      toast.error("Reset failed");
    } finally { setSaving(false); }
  };

  const update = (field, value) => setPrompt(p => ({ ...p, [field]: value }));

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Interview Prompts</h2>
        <p className="text-sm text-slate-500">Each round has a global default. Pick a company to create or edit a per-company override.</p>
      </div>

      {/* Selectors */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Round</label>
          <select
            value={kind} onChange={(e) => setKind(e.target.value)}
            className="w-full p-2 border border-slate-300 rounded-md"
          >
            {KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Scope</label>
          <select
            value={companyId} onChange={(e) => setCompanyId(e.target.value)}
            className="w-full p-2 border border-slate-300 rounded-md"
          >
            <option value="">— Global default —</option>
            {companies.map(c => <option key={c._id} value={c._id}>{c.name || c.companyName || c._id}</option>)}
          </select>
        </div>
      </div>

      {/* Editor */}
      {loading && (
        <div className="p-8 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      )}

      {!loading && prompt && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500">Version {prompt.version || 1} · {prompt.companyId ? "Company override" : "Global default"}</div>
              {prompt.elevenAgentId && <div className="text-xs text-emerald-600 font-mono mt-1">Agent: {prompt.elevenAgentId}</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={reset} disabled={saving} className="px-3 py-2 text-sm text-slate-600 hover:text-rose-600 flex items-center gap-2">
                <RotateCcw className="w-4 h-4" /> {companyId ? "Remove override" : "Reset to default"}
              </button>
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-40 flex items-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>

          <Field label="Label">
            <input value={prompt.label || ""} onChange={(e) => update("label", e.target.value)}
              className="w-full p-2 border border-slate-300 rounded-md" />
          </Field>

          <Field label="Opener" subtitle="The first thing the agent says. Use {{candidateName}} and {{jobTitle}}.">
            <textarea rows={3} value={prompt.opener || ""} onChange={(e) => update("opener", e.target.value)}
              className="w-full p-2 border border-slate-300 rounded-md font-mono text-sm" />
          </Field>

          <Field label="System Prompt" subtitle="Persona + guardrails + interview goal. The agent's brain.">
            <textarea rows={18} value={prompt.systemPrompt || ""} onChange={(e) => update("systemPrompt", e.target.value)}
              className="w-full p-2 border border-slate-300 rounded-md font-mono text-xs leading-relaxed" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Question target">
              <input type="number" min={1} max={30} value={prompt.questionTarget || 8}
                onChange={(e) => update("questionTarget", Number(e.target.value))}
                className="w-full p-2 border border-slate-300 rounded-md" />
            </Field>
            <Field label="Duration (minutes)">
              <input type="number" min={5} max={120} value={prompt.durationMinutes || 20}
                onChange={(e) => update("durationMinutes", Number(e.target.value))}
                className="w-full p-2 border border-slate-300 rounded-md" />
            </Field>
            <Field label="Grading axes" subtitle="Comma-separated">
              <input value={axesInput} onChange={(e) => setAxesInput(e.target.value)}
                placeholder="Technical, Communication, ..."
                className="w-full p-2 border border-slate-300 rounded-md text-sm" />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, subtitle, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      {subtitle && <p className="text-xs text-slate-500 mb-2">{subtitle}</p>}
      {children}
    </div>
  );
}
